import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ImageNotSupportedRoundedIcon from '@mui/icons-material/ImageNotSupportedRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import DashboardPageHeader from 'pages/dashboard/DashboardPageHeader';
import VendorDashboardLayout from 'pages/dashboard/VendorDashboardLayout';
import { formatOrderMoney, getOrderNumber } from './orderUtils';
import { groupProductOptions, resolveProductVariant } from './orderProductOptions';

const DETAIL_FIELDS = [
  'firstName',
  'surname',
  'phone',
  'email',
  'country',
  'streetAddress',
  'city',
  'county',
  'postcode',
  'paymentType',
  'shippingType',
  'shippingPrice',
  'note'
];
const BGN_PER_EUR = 1.95583;

const priceInOrderCurrency = (value, itemCurrency, orderCurrency) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 0;
  const fromCurrency = String(itemCurrency || 'BGN').toUpperCase();
  const toCurrency = String(orderCurrency || 'BGN').toUpperCase();
  if (fromCurrency === toCurrency) return amount;
  return fromCurrency === 'EUR' ? amount * BGN_PER_EUR : amount / BGN_PER_EUR;
};

const createDetails = (order = {}) =>
  Object.fromEntries(DETAIL_FIELDS.map((field) => [field, order[field] ?? '']));

function ProductImage({ item }) {
  return item.imageUrl ? (
    <Box
      component="img"
      src={item.imageUrl}
      alt={item.productName}
      sx={{ borderRadius: 2, height: 62, objectFit: 'cover', width: 62, flexShrink: 0 }}
    />
  ) : (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'grey.100',
        borderRadius: 2,
        color: 'grey.400',
        display: 'flex',
        height: 62,
        justifyContent: 'center',
        width: 62,
        flexShrink: 0
      }}
    >
      <ImageNotSupportedRoundedIcon />
    </Box>
  );
}

function MaterialImage({ material }) {
  return material?.imageUrl ? (
    <Box
      component="img"
      src={material.imageUrl}
      alt={`Цвят ${material.code}`}
      sx={{ borderRadius: 2, height: 46, objectFit: 'cover', width: 46, flexShrink: 0 }}
    />
  ) : (
    <Box
      sx={{
        alignItems: 'center',
        bgcolor: 'grey.100',
        borderRadius: 2,
        color: 'grey.400',
        display: 'flex',
        height: 46,
        justifyContent: 'center',
        width: 46,
        flexShrink: 0
      }}
    >
      <ImageNotSupportedRoundedIcon fontSize="small" />
    </Box>
  );
}

function Attributes({ attributes = [] }) {
  if (!attributes.length) return null;
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.75}>
      {attributes.map((attribute) => (
        <Chip
          key={`${attribute.label}-${attribute.value}`}
          size="small"
          variant="outlined"
          label={`${attribute.label}: ${attribute.value}`}
        />
      ))}
    </Stack>
  );
}

export default function OrderEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [details, setDetails] = useState(createDetails());
  const [items, setItems] = useState([]);
  const [materialAllocations, setMaterialAllocations] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [selectedMaterialId, setSelectedMaterialId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [productSelections, setProductSelections] = useState({});
  const [productCategories, setProductCategories] = useState([]);
  const [selectedProductCategoryId, setSelectedProductCategoryId] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [productError, setProductError] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [isEmailPreviewLoading, setIsEmailPreviewLoading] = useState(false);
  const [emailPreviewError, setEmailPreviewError] = useState('');
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailToast, setEmailToast] = useState(null);
  const emailPreviewRequested = useRef(false);

  useEffect(() => {
    let isCurrent = true;
    Promise.all([axios.get(`/orders/${id}`), axios.get('/materials')])
      .then(([orderResponse, materialsResponse]) => {
        if (!isCurrent) return;
        const { data } = orderResponse;
        setOrder(data.order);
        setDetails(createDetails(data.order));
        setItems(data.items || []);
        setMaterialOptions(materialsResponse.data.materials || []);
        setMaterialAllocations(
          (data.materialAllocations || []).map((allocation) => ({
            materialInventoryId: allocation.materialInventoryId,
            meters: String(allocation.meters),
            material: allocation.material
          }))
        );
      })
      .catch((requestError) => {
        if (isCurrent) {
          setError(
            requestError.response?.status === 404
              ? 'Тази поръчка не беше намерена.'
              : 'Поръчката не можа да бъде заредена.'
          );
        }
      })
      .finally(() => {
        if (isCurrent) setIsLoading(false);
      });
    return () => {
      isCurrent = false;
    };
  }, [id]);

  const searchProducts = async (event) => {
    event?.preventDefault();
    setIsSearching(true);
    setProductError('');
    try {
      const { data } = await axios.get('/order-product-options', {
        params: {
          search: productSearch.trim() || undefined,
          subCategoryId: selectedProductCategoryId || undefined
        }
      });
      setProductOptions(data.options || []);
      setProductSelections({});
    } catch (requestError) {
      setProductError('Продуктите не можаха да бъдат заредени.');
    } finally {
      setIsSearching(false);
    }
  };

  const openProductDialog = () => {
    setDialogOpen(true);
    setProductError('');
    if (!productCategories.length) {
      axios
        .get('/subcategory')
        .then(({ data }) => setProductCategories(data || []))
        .catch(() => setProductCategories([]));
    }
  };

  const addProduct = (option) => {
    if (items.some((item) => Number(item.priceId) === Number(option.priceId))) return;
    setItems((current) => [...current, { ...option, id: `new-${option.priceId}`, quantity: 1 }]);
    setEmailToast({
      severity: 'success',
      message: `Продуктът „${option.productName}“ беше добавен към поръчката.`
    });
  };

  const productGroups = useMemo(() => groupProductOptions(productOptions), [productOptions]);

  const updateProductSelection = (group, label, value) => {
    setProductSelections((current) => {
      const nextSelection = { ...(current[group.id] || {}), [label]: value };
      const hasMatchingVariant = group.variants.some((variant) =>
        Object.entries(nextSelection).every(
          ([selectedLabel, selectedValue]) =>
            !selectedValue || variant.attributeValues[selectedLabel] === selectedValue
        )
      );

      // A new dimension can make an earlier choice invalid. Clear the other choices so the
      // administrator can immediately select a compatible size instead of being stuck with an
      // empty price result.
      if (!hasMatchingVariant) {
        Object.keys(nextSelection).forEach((selectedLabel) => {
          if (selectedLabel !== label) delete nextSelection[selectedLabel];
        });
      }

      return { ...current, [group.id]: nextSelection };
    });
  };

  const updateQuantity = (index, value) => {
    const quantity = Math.max(1, Math.min(999, Number.parseInt(value, 10) || 1));
    setItems((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, quantity } : item))
    );
  };

  const subtotal = useMemo(
    () =>
      items.reduce((sum, item) => {
        return (
          sum +
          priceInOrderCurrency(item.unitPrice, item.currency, order?.currency) *
            Number(item.quantity || 0)
        );
      }, 0),
    [items, order?.currency]
  );
  const shippingPrice = Number(String(details.shippingPrice || '').replace(',', '.'));
  const total = subtotal + (Number.isFinite(shippingPrice) ? shippingPrice : 0);

  const updateDetail = (field) => (event) => {
    setDetails((current) => ({ ...current, [field]: event.target.value }));
  };

  const addMaterial = () => {
    const materialInventoryId = Number(selectedMaterialId);
    const material = materialOptions.find((option) => option.id === materialInventoryId);
    if (!material || materialAllocations.some((item) => item.materialInventoryId === materialInventoryId)) {
      return;
    }
    setMaterialAllocations((current) => [
      ...current,
      { materialInventoryId, meters: '', material }
    ]);
    setSelectedMaterialId('');
  };

  const updateMaterialMeters = (index, meters) => {
    setMaterialAllocations((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, meters } : item))
    );
  };

  const previewUpdatedEmail = async () => {
    setEmailDialogOpen(true);
    setEmailPreview(null);
    setEmailPreviewError('');
    setEmailSent(false);
    setIsEmailPreviewLoading(true);
    try {
      const { data } = await axios.get(`/orders/${id}/updated-email-preview`);
      setEmailPreview(data);
    } catch (requestError) {
      setEmailPreviewError(
        requestError.response?.data?.message || 'Прегледът на имейла не можа да бъде зареден.'
      );
    } finally {
      setIsEmailPreviewLoading(false);
    }
  };

  const sendUpdatedEmail = async () => {
    setIsSendingEmail(true);
    setEmailPreviewError('');
    try {
      const { data } = await axios.post(`/orders/${id}/updated-email`);
      setEmailSent(true);
      const adminRecipients = Array.isArray(data.adminRecipients)
        ? data.adminRecipients.filter(Boolean).join(', ')
        : '';
      setEmailToast({
        severity: data.adminNotificationSent === false ? 'warning' : 'success',
        message:
          data.adminNotificationSent === false
            ? `Имейлът беше изпратен до ${data.to}, но копието до администраторите не успя.`
            : `Актуализираният имейл беше изпратен успешно до ${data.to}${
                adminRecipients ? ` и до администраторите: ${adminRecipients}.` : '.'
              }`
      });
    } catch (requestError) {
      setEmailPreviewError(
        requestError.response?.data?.message || 'Имейлът не можа да бъде изпратен.'
      );
    } finally {
      setIsSendingEmail(false);
    }
  };

  const saveOrder = async (event) => {
    event.preventDefault();
    const shouldPreviewUpdatedEmail = emailPreviewRequested.current;
    emailPreviewRequested.current = false;
    setError('');
    if (!details.firstName.trim() || !details.phone.trim() || !details.email.trim()) {
      setError('Име, телефон и имейл са задължителни.');
      return;
    }
    if (!items.length) {
      setError('Поръчката трябва да съдържа поне един продукт.');
      return;
    }
    if (!/^\d+(?:[.,]\d{1,2})?$/.test(String(details.shippingPrice))) {
      setError('Въведете валидна неотрицателна цена за доставка.');
      return;
    }
    if (
      materialAllocations.some(
        (allocation) =>
          !/^\d+(?:[.,]\d{1,3})?$/.test(String(allocation.meters)) ||
          Number(String(allocation.meters).replace(',', '.')) <= 0
      )
    ) {
      setError('За всеки материал въведете брой метри над нула (до 3 знака след запетаята).');
      return;
    }

    setIsSaving(true);
    try {
      const { data } = await axios.put(`/orders/${id}`, {
        version: order.version || 0,
        details,
        items: items.map((item) => ({
          itemId: Number.isInteger(Number(item.id)) ? Number(item.id) : null,
          priceId: item.priceId,
          quantity: item.quantity
        })),
        materials: materialAllocations.map((allocation) => ({
          materialInventoryId: allocation.materialInventoryId,
          meters: allocation.meters
        }))
      });
      if (data.order) setOrder(data.order);
      if (data.items) setItems(data.items);
      if (shouldPreviewUpdatedEmail) {
        await previewUpdatedEmail();
      } else {
        navigate(`/orderDetails/${id}`);
      }
    } catch (requestError) {
      setError(
        requestError.response?.status === 409
          ? 'Поръчката е променена от друг администратор. Върнете се към детайла и опитайте отново.'
          : requestError.response?.data?.message || 'Поръчката не можа да бъде запазена.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <VendorDashboardLayout>
        <Card sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress aria-label="Зареждане на поръчката" />
        </Card>
      </VendorDashboardLayout>
    );
  }

  if (!order) {
    return (
      <VendorDashboardLayout>
        <Alert severity="error">{error || 'Тази поръчка не беше намерена.'}</Alert>
      </VendorDashboardLayout>
    );
  }

  return (
    <VendorDashboardLayout>
      <DashboardPageHeader
        title={`Редакция на поръчка #${getOrderNumber(order)}`}
        subtitle="Всички промени ще бъдат записани в историята на поръчката."
        icon={EditRoundedIcon}
        actions={
          <Button component={Link} to={`/orderDetails/${id}`} startIcon={<ArrowBackRoundedIcon />}>
            Отказ
          </Button>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={saveOrder}>
        <Grid container spacing={3} alignItems="flex-start">
          <Grid item xs={12} lg={7.5}>
            <Stack spacing={3}>
              <Card sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" mb={2.5}>
                  Данни за клиента
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Име"
                      value={details.firstName}
                      onChange={updateDetail('firstName')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Фамилия"
                      value={details.surname}
                      onChange={updateDetail('surname')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      label="Телефон"
                      value={details.phone}
                      onChange={updateDetail('phone')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Имейл"
                      value={details.email}
                      onChange={updateDetail('email')}
                    />
                  </Grid>
                </Grid>
              </Card>

              <Card sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" mb={2.5}>
                  Адрес и доставка
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Държава"
                      value={details.country}
                      onChange={updateDetail('country')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Град"
                      value={details.city}
                      onChange={updateDetail('city')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Адрес"
                      value={details.streetAddress}
                      onChange={updateDetail('streetAddress')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Област"
                      value={details.county}
                      onChange={updateDetail('county')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Пощенски код"
                      value={details.postcode}
                      onChange={updateDetail('postcode')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      label="Начин на доставка"
                      value={details.shippingType}
                      onChange={updateDetail('shippingType')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      required
                      label={`Цена за доставка (${String(order.currency || 'BGN').toUpperCase() === 'EUR' ? '€' : 'лв.'})`}
                      value={details.shippingPrice}
                      onChange={updateDetail('shippingPrice')}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>
                </Grid>
              </Card>

              <Card sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography variant="h6" mb={2.5}>
                  Плащане и бележка
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="Начин на плащане"
                    value={details.paymentType}
                    onChange={updateDetail('paymentType')}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    label="Бележка"
                    value={details.note}
                    onChange={updateDetail('note')}
                  />
                </Stack>
              </Card>
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4.5}>
            <Stack spacing={3}>
            <Card sx={{ overflow: 'hidden' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{ p: 3 }}
              >
                <Typography variant="h6">Продукти ({items.length})</Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddRoundedIcon />}
                  onClick={openProductDialog}
                >
                  Добави
                </Button>
              </Stack>
              <Divider />
              <Stack divider={<Divider />}>
                {items.map((item, index) => (
                  <Box key={`${item.id}-${item.priceId}`} sx={{ p: 2.5 }}>
                    <Stack direction="row" gap={1.5} alignItems="flex-start">
                      <ProductImage item={item} />
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography fontWeight={700}>{item.productName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.productCode || `Цена #${item.priceId}`}
                        </Typography>
                        <Attributes attributes={item.attributes} />
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label={`Премахни ${item.productName}`}
                        onClick={() =>
                          setItems((current) =>
                            current.filter((_, itemIndex) => itemIndex !== index)
                          )
                        }
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                      mt={2}
                    >
                      <TextField
                        type="number"
                        label="Количество"
                        value={item.quantity}
                        onChange={(event) => updateQuantity(index, event.target.value)}
                        inputProps={{ min: 1, max: 999 }}
                        sx={{ width: 125 }}
                      />
                      <Typography fontWeight={800}>
                        {formatOrderMoney(
                          Number(item.unitPrice) * Number(item.quantity),
                          item.currency
                        )}
                      </Typography>
                    </Stack>
                  </Box>
                ))}
                {!items.length && (
                  <Typography color="error" sx={{ p: 3 }}>
                    Добавете поне един продукт.
                  </Typography>
                )}
              </Stack>
              <Divider />
              <Stack spacing={1.25} sx={{ bgcolor: 'grey.50', p: 3 }}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Междинна сума</Typography>
                  <Typography fontWeight={700}>
                    {formatOrderMoney(subtotal, order.currency)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography color="text.secondary">Доставка</Typography>
                  <Typography>
                    {formatOrderMoney(
                      Number.isFinite(shippingPrice) ? shippingPrice : 0,
                      order.currency
                    )}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={700}>Общо</Typography>
                  <Typography fontWeight={800}>{formatOrderMoney(total, order.currency)}</Typography>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={<SaveRoundedIcon />}
                    disabled={isSaving}
                    fullWidth
                    onClick={() => {
                      emailPreviewRequested.current = false;
                    }}
                  >
                    {isSaving ? 'Запазване…' : 'Запази и отпиши материали'}
                  </Button>
                  <Button
                    type="submit"
                    name="updatedEmail"
                    value="true"
                    variant="outlined"
                    size="large"
                    startIcon={<EmailRoundedIcon />}
                    disabled={isSaving}
                    fullWidth
                    onClick={() => {
                      emailPreviewRequested.current = true;
                    }}
                  >
                    Запази и прегледай имейла
                  </Button>
                </Stack>
              </Stack>
            </Card>

            <Card sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6">Материали за поръчката</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    При запазване въведените метри се отписват от наличността.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  <FormControl fullWidth>
                    <InputLabel id="order-material-label">Добави материал</InputLabel>
                    <Select
                      labelId="order-material-label"
                      label="Добави материал"
                      value={selectedMaterialId}
                      onChange={(event) => setSelectedMaterialId(event.target.value)}
                    >
                      {materialOptions.map((material) => {
                        const alreadyAdded = materialAllocations.some(
                          (allocation) => allocation.materialInventoryId === material.id
                        );
                        return (
                          <MenuItem key={material.id} value={material.id} disabled={alreadyAdded}>
                            {material.code} · {material.availableMeters} м налични
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <Button type="button" variant="outlined" onClick={addMaterial} disabled={!selectedMaterialId}>
                    Добави
                  </Button>
                </Stack>

                <Stack spacing={1.25}>
                  {materialAllocations.map((allocation, index) => (
                    <Stack
                      key={allocation.materialInventoryId}
                      direction="row"
                      alignItems="center"
                      gap={1.25}
                      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.25 }}
                    >
                      <MaterialImage material={allocation.material} />
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography fontWeight={700}>{allocation.material?.code}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Налични: {allocation.material?.availableMeters ?? 0} м
                        </Typography>
                      </Box>
                      <TextField
                        label="Метри"
                        value={allocation.meters}
                        onChange={(event) => updateMaterialMeters(index, event.target.value)}
                        inputProps={{ inputMode: 'decimal' }}
                        sx={{ width: 105 }}
                      />
                      <IconButton
                        type="button"
                        color="error"
                        size="small"
                        aria-label={`Премахни материал ${allocation.material?.code || ''}`}
                        onClick={() =>
                          setMaterialAllocations((current) =>
                            current.filter((_, allocationIndex) => allocationIndex !== index)
                          )
                        }
                      >
                        <DeleteOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  {!materialAllocations.length && (
                    <Typography variant="body2" color="text.secondary">
                      Добавете материал само ако тази поръчка трябва да отпише плат от склада.
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </Card>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Добавяне на продукт</DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={searchProducts} sx={{ mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
              <TextField
                autoFocus
                fullWidth
                label="Име или код на продукт"
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon />
                    </InputAdornment>
                  )
                }}
              />
              <FormControl sx={{ minWidth: { sm: 230 } }}>
                <InputLabel id="order-product-category-label">Категория</InputLabel>
                <Select
                  labelId="order-product-category-label"
                  label="Категория"
                  value={selectedProductCategoryId}
                  onChange={(event) => setSelectedProductCategoryId(event.target.value)}
                >
                  <MenuItem value="">Всички категории</MenuItem>
                  {productCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name_bg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button type="submit" variant="contained" disabled={isSearching}>
                Търси
              </Button>
            </Stack>
          </Box>
          {productError && <Alert severity="error">{productError}</Alert>}
          {isSearching ? (
            <Stack alignItems="center" sx={{ p: 5 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <Stack divider={<Divider />}>
              {productGroups.map((group) => {
                const selections = productSelections[group.id] || {};
                const option = resolveProductVariant(group, selections);
                const alreadyAdded = option && items.some(
                  (item) => Number(item.priceId) === Number(option.priceId)
                );
                return (
                  <Stack
                    key={group.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    gap={2}
                    sx={{ py: 2 }}
                  >
                    <ProductImage item={group} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography fontWeight={700}>{group.productName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {group.productCode}
                      </Typography>
                      {group.subcategoryName && (
                        <Chip
                          label={group.subcategoryName}
                          color="primary"
                          size="small"
                          sx={{ display: 'flex', mt: 0.75, width: 'fit-content' }}
                        />
                      )}
                      {group.attributes.length > 0 ? (
                        <Stack direction={{ xs: 'column', md: 'row' }} gap={1} mt={1.25}>
                          {group.attributes.map((attribute) => (
                            <FormControl key={attribute.label} size="small" sx={{ minWidth: 150 }}>
                              <InputLabel id={`${group.id}-${attribute.label}-label`}>
                                {attribute.label}
                              </InputLabel>
                              <Select
                                labelId={`${group.id}-${attribute.label}-label`}
                                label={attribute.label}
                                value={selections[attribute.label] || ''}
                                onChange={(event) =>
                                  updateProductSelection(group, attribute.label, event.target.value)
                                }
                              >
                                <MenuItem value="">
                                  <em>Изберете</em>
                                </MenuItem>
                                {attribute.values.map((value) => (
                                  <MenuItem key={value} value={value}>
                                    {value}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ))}
                        </Stack>
                      ) : (
                        <Attributes attributes={group.variants[0]?.attributes} />
                      )}
                      {option && (
                        <Typography mt={0.75} fontWeight={800}>
                          {formatOrderMoney(option.unitPrice, option.currency)}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      disabled={!option || alreadyAdded}
                      onClick={() => addProduct(option)}
                    >
                      {alreadyAdded ? 'Добавен' : 'Добави'}
                    </Button>
                  </Stack>
                );
              })}
              {!productGroups.length && !productError && (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                  Въведете код или име на продукт, за да изберете неговия размер.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Затвори</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={emailDialogOpen}
        onClose={() => !isSendingEmail && navigate(`/orderDetails/${id}`)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Преглед на актуализиран имейл</DialogTitle>
        <DialogContent dividers>
          {isEmailPreviewLoading && (
            <Stack alignItems="center" sx={{ p: 6 }}>
              <CircularProgress />
            </Stack>
          )}
          {emailPreviewError && <Alert severity="error">{emailPreviewError}</Alert>}
          {emailPreview && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                До: {emailPreview.to} · Тема: {emailPreview.subject}
              </Typography>
              {Array.isArray(emailPreview.adminRecipients) &&
                emailPreview.adminRecipients.length > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Копие с известие ще получат администраторите: {emailPreview.adminRecipients.join(', ')}
                  </Typography>
                )}
              <Box
                component="iframe"
                title="Преглед на актуализирания имейл"
                srcDoc={emailPreview.html}
                sandbox=""
                sx={{ border: '1px solid', borderColor: 'divider', height: 600, width: '100%' }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`/orderDetails/${id}`)} disabled={isSendingEmail}>
            {emailSent ? 'Към поръчката' : 'Пропусни'}
          </Button>
          <Button
            variant="contained"
            startIcon={<EmailRoundedIcon />}
            onClick={sendUpdatedEmail}
            disabled={!emailPreview || isSendingEmail || emailSent}
          >
            {emailSent ? 'Изпратен' : isSendingEmail ? 'Изпращане…' : 'Изпрати сега'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(emailToast)}
        autoHideDuration={6000}
        onClose={() => setEmailToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={emailToast?.severity || 'success'} onClose={() => setEmailToast(null)}>
          {emailToast?.message}
        </Alert>
      </Snackbar>
    </VendorDashboardLayout>
  );
}
